/**
 * Deploy-target identification.
 *
 * NODE_ENV cannot answer this: `craco build` forces it to 'production' for
 * every deploy target, so test, UAT, sandbox and production builds all report
 * the same value and analytics cannot tell them apart. Only localhost differed,
 * which is the signature of NODE_ENV rather than of a real environment flag.
 *
 * REACT_APP_ENVIRONMENT is set per step in bitbucket-pipelines.yml and is the
 * authoritative source. The hostname fallback keeps the value correct for local
 * dev and for any build made before that variable existed.
 */

const DEPLOY_ENVIRONMENTS = Object.freeze({
    LOCAL: 'local',
    TEST: 'test',
    UAT: 'uat',
    PRODUCTION: 'production',
});

/**
 * Hostname -> environment. Order matters: the first match wins. Anything
 * unmatched is production, so a new production domain is never mislabelled as
 * non-production simply because nobody added it here.
 */
const HOST_ENVIRONMENT_PATTERNS = [
    [/^localhost$/, DEPLOY_ENVIRONMENTS.LOCAL],
    [/^127\.0\.0\.1$/, DEPLOY_ENVIRONMENTS.LOCAL],
    [/\.local$/, DEPLOY_ENVIRONMENTS.LOCAL],
    [/-test\./, DEPLOY_ENVIRONMENTS.TEST],
    [/-dev\./, DEPLOY_ENVIRONMENTS.TEST],
    [/-uat\./, DEPLOY_ENVIRONMENTS.UAT],
];

/**
 * Environment inferred from the hostname alone.
 *
 * Deliberately ignores REACT_APP_ENVIRONMENT: this is what the consent gate
 * uses, and the gate must reflect where the app is actually running rather
 * than what a build-time variable claims. A mistyped variable must not be able
 * to make a production host look like test and fail open.
 */
const getHostEnvironment = () => {
    if (typeof window === 'undefined') return DEPLOY_ENVIRONMENTS.PRODUCTION;
    const hostname = window.location?.hostname || '';
    const match = HOST_ENVIRONMENT_PATTERNS.find(([pattern]) => pattern.test(hostname));
    return match ? match[1] : DEPLOY_ENVIRONMENTS.PRODUCTION;
};

/** True when the app is running on a host that is not a known non-production one. */
const isProductionHost = () => getHostEnvironment() === DEPLOY_ENVIRONMENTS.PRODUCTION;

/**
 * The deploy target to report on analytics events.
 *
 * Prefers the explicit build-time variable so values like 'sandbox' — which no
 * hostname rule covers — come through as configured, and falls back to the
 * hostname when it is unset.
 */
const getDeployEnvironment = () => {
    const explicit = process.env.REACT_APP_ENVIRONMENT;
    if (typeof explicit === 'string' && explicit.trim() !== '') {
        return explicit.trim().toLowerCase();
    }
    return getHostEnvironment();
};

export { DEPLOY_ENVIRONMENTS, getDeployEnvironment, getHostEnvironment, isProductionHost };
